import dayjs from '~/timeInit';

import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import type {
  ScoredTicketRisk,
  TicketRiskBand,
  TicketRiskHardBreakdown,
  TicketRiskReport,
  TicketRiskTierChange,
  TicketRiskTrajectory,
  TicketRiskWatch,
} from '~/types/ticketRisk';

export const EMPTY_VALUE = '-';

const ZENDESK_TICKET_URL = 'https://luxuryescapeshelp.zendesk.com/agent/tickets';
const ZENDESK_BRAND_ID = '11031997673999';

export type RiskTier = 'Critical' | 'High' | 'Medium' | 'Low';

/**
 * In-stay and Pre-stay come straight from the customer context; svc-support carries no
 * post-stay signal, so anything without an active or upcoming booking is Unknown rather
 * than being guessed at.
 */
export type JourneyPhase = 'In-stay' | 'Pre-stay' | 'Unknown';

export const RISK_TIERS: Array<RiskTier> = ['Critical', 'High', 'Medium', 'Low'];
export const JOURNEY_PHASES: Array<JourneyPhase> = ['In-stay', 'Pre-stay', 'Unknown'];

const BAND_TO_TIER: Record<TicketRiskBand, RiskTier> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** Société tiers the MUI theme has `palette.loyaltyTiers` entries for. */
export const SOCIETE_TIER_THEME_KEYS = {
  Bronze: 'bronze',
  Silver: 'silver',
  Gold: 'gold',
  Platinum: 'platinum',
} as const;

export type SocieteTier = keyof typeof SOCIETE_TIER_THEME_KEYS;

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
const SHORT_DATE_FORMAT = 'DD MMM';
const SHORT_DATE_TIME_FORMAT = 'DD MMM HH:mm';

/** Tier → MUI palette key, so the tier reads the same on every chip, dot and bar. */
export const RISK_TIER_COLORS: Record<RiskTier, 'error' | 'warning' | 'info' | 'success'> = {
  Critical: 'error',
  High: 'warning',
  Medium: 'info',
  Low: 'success',
};

export interface CxTicketRow {
  id: string;
  subject: string | null;

  // Scores
  riskScore: number;
  riskTier: RiskTier;
  /**
   * Entered High/Critical since yesterday's run — was Low, Medium or unseen (every High/Critical
   * ticket, on svc-support's first day). High → Critical is an up-move, not New.
   */
  isNew: boolean;
  /** Null only when svc-support predates the tier-change fields. */
  tierChange: TicketRiskTierChange | null;
  /** The tier in yesterday's run; null when the ticket was not scored then. */
  previousTier: RiskTier | null;
  hardScore: number;
  llmScore: number | null;
  divergent: boolean;
  percentile: number | null;
  criticalRank: number | null;
  /** False when the LLM call failed for this ticket and the hard score carried the blend. */
  hasLlmAssessment: boolean;
  llmTrajectory: TicketRiskTrajectory | null;
  llmConfidence: number | null;
  llmReasoning: string | null;
  llmRiskFactors: Array<string>;
  llmEarlyWarnings: Array<string>;
  /** The LLM found a grounded acceptance of the resolution — excluded from critical. */
  resolutionAccepted: boolean;

  // Watching and comments
  /** The one leader handling the ticket; null when nobody is. */
  watcher: TicketRiskWatch | null;
  /** The watcher's name or '' — what the grid sorts/filters on and the CSV exports. */
  watcherName: string;
  /** True when the signed-in leader is the watcher — drives Watch / Take over / Stop watching. */
  isWatchedByMe: boolean;
  /** Live comments on the ticket's thread; 0 when svc-support predates comments. */
  commentCount: number;
  /** Of those, posted in the last hour — what the grid badges. */
  recentCommentCount: number;
  /** Who commented last and when; null when nobody has. */
  lastComment: { authorName: string; createdAt: string } | null;

  // Hard score components
  classificationScore: number;
  /** The classification that scored, e.g. `reason:Complaint`. */
  classificationSource: string | null;
  classificationLift: number | null;
  riskFlagScore: number;
  matchedRiskFlags: Array<string>;
  keywordSignalScore: number;
  matchedKeywordSignals: Array<string>;
  /** A legal / fair-trading ownership marker was found — the ticket is forced to critical. */
  legalHold: boolean;
  reassignPoints: number;
  agingPoints: number;
  awaitingReviewPoints: number;
  requesterWaitPoints: number;
  staleContactPoints: number;
  internalCommentPoints: number;
  reopenPoints: number;
  operationalRiskScore: number;
  inStayPoints: number;
  upcomingCheckInPoints: number;
  recentStayPoints: number;
  ltvPoints: number;
  societePoints: number;
  luxPlusPoints: number;
  customerRiskScore: number;

  // Ticket
  created: string;
  lastUpdated: string | null;
  statusUpdated: string | null;
  ticketStatus: string;
  team: string | null;
  assignee: string | null;
  teamLeader: string | null;
  ticketReason: string | null;
  subcategory: string | null;
  triageCategory: string | null;
  priority: string | null;
  tags: Array<string>;
  replies: number;
  internalComments: number;
  agentsTouched: number;
  reassignCount: number;
  reopenCount: number;
  requesterWaitMinutes: number;
  caseAgeDays: number;
  hoursSinceOpen: number;
  hoursAwaitingReview: number;
  hoursSinceLastContact: number;
  lastAgentReplyAt: string | null;
  /** Display label, e.g. "Very negative". */
  sentiment: string | null;
  /** Collapsed to three buckets so the sentiment filters and counters can compare exactly. */
  sentimentBucket: SentimentBucket | null;

  // Customer
  journeyPhase: JourneyPhase;
  checkInDays: number | null;
  societeTier: string | null;
  luxPlusMember: boolean | null;
  customerLifetimeValue: number | null;
  ltvBand: string | null;
  /** Days since the customer's last checkout; null when they have never stayed. */
  daysSinceLastCheckOut: number | null;
  authorityKeywords: Array<string>;
  authorityConfirmed: boolean;
}

export function bandToTier(band: TicketRiskBand): RiskTier {
  return BAND_TO_TIER[band];
}

/**
 * One line on how the tier moved since yesterday's run, for the New chip's tooltip and the
 * detail dialog. Null when svc-support served no tier change (it predates the fields).
 */
export function describeTierChange(row: Pick<CxTicketRow, 'riskTier' | 'tierChange' | 'previousTier'>): string | null {
  switch (row.tierChange) {
    case 'new':
      return 'Not scored yesterday';
    case 'up':
      return `Up from ${row.previousTier} yesterday`;
    case 'down':
      return `Down from ${row.previousTier} yesterday`;
    case 'same':
      return `${row.riskTier} yesterday too`;
    default:
      return null;
  }
}

/** `very_negative` → `Very negative`; used for sentiment, LTV bands and authority keywords. */
export function humanize(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  if (!spaced) {
    return null;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Display label for the ticket's state. Prefers the Zendesk custom status ("Awaiting Review")
 * because that is what CX works from, falling back to the native status when there is none.
 */
export function toTicketStatusLabel(status: string, customStatusName: string | null): string {
  return customStatusName ?? humanize(status) ?? status;
}

export function toJourneyPhase(inStayNow: boolean, upcomingCheckInDays: number | null): JourneyPhase {
  if (inStayNow) {
    return 'In-stay';
  }
  if (upcomingCheckInDays !== null) {
    return 'Pre-stay';
  }
  return 'Unknown';
}

export type SentimentBucket = 'Positive' | 'Neutral' | 'Negative';

/**
 * Zendesk emits several negative grades (`negative`, `very_negative`); the dashboard only
 * ever asks "is this negative", so they collapse into one bucket.
 */
export function toSentimentBucket(zendeskSentiment: string | null): SentimentBucket | null {
  if (!zendeskSentiment) {
    return null;
  }
  const normalized = zendeskSentiment.toLowerCase();
  if (normalized.includes('negative')) {
    return 'Negative';
  }
  if (normalized.includes('positive')) {
    return 'Positive';
  }
  return 'Neutral';
}

export function isSocieteTier(value: string | null): value is SocieteTier {
  return value !== null && value in SOCIETE_TIER_THEME_KEYS;
}

/** svc-support sends Société tiers lower-cased; the theme keys and labels are title-cased. */
export function toSocieteTierLabel(societeTier: string | null): string | null {
  return humanize(societeTier);
}

/** ISO instant → epoch ms, or null when absent/unparseable. Used for date bucketing. */
export function toTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(DATE_TIME_FORMAT) : EMPTY_VALUE;
}

/** Axis-friendly day and month, e.g. `19 Aug`. Accepts an ISO instant or epoch milliseconds. */
export function formatShortDate(value: string | number | null | undefined): string {
  if (value == null || value === '') {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(SHORT_DATE_FORMAT) : EMPTY_VALUE;
}

/** As `formatShortDate`, with the time — the scoring job runs several times a day. */
export function formatShortDateTime(value: string | number | null | undefined): string {
  if (value == null || value === '') {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(SHORT_DATE_TIME_FORMAT) : EMPTY_VALUE;
}

/** Durations render as hours below a day and as days above it, so long-running tickets stay readable. */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) {
    return EMPTY_VALUE;
  }
  if (hours < HOURS_PER_DAY) {
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round((hours / HOURS_PER_DAY) * 10) / 10;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export function formatMinutesAsDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) {
    return EMPTY_VALUE;
  }
  if (minutes < MINUTES_PER_HOUR) {
    const rounded = Math.round(minutes);
    return `${rounded} ${rounded === 1 ? 'minute' : 'minutes'}`;
  }
  return formatHours(minutes / MINUTES_PER_HOUR);
}

export function formatCount(count: number | null | undefined, singular: string, plural = `${singular}s`): string {
  if (!count) {
    return EMPTY_VALUE;
  }
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Renders anything the UI has no value for as a dash rather than `null` or `NaN`. */
export function formatEmptyValue(value: unknown): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return EMPTY_VALUE;
}

export function formatCheckIn(checkInDays: number | null): string {
  if (checkInDays == null) {
    return EMPTY_VALUE;
  }

  if (checkInDays <= 0) {
    return 'Today';
  }

  return `In ${formatCount(checkInDays, 'day')}`;
}

/**
 * Joins the parts of a compound field, dropping the ones with nothing in them, so a field
 * reads "Complaint" rather than "Complaint · -".
 */
export function joinDetails(parts: Array<string | null | undefined>, separator = ' · '): string {
  const present = parts.filter((part): part is string => typeof part === 'string' && part.trim().length > 0);

  return present.length > 0 ? present.join(separator) : EMPTY_VALUE;
}

export function formatAud(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return EMPTY_VALUE;
  }
  return `$${Math.round(amount).toLocaleString('en-AU')}`;
}

/** `percentile` is 0 at the riskiest ticket, so it reads better as "top N%". */
export function formatPercentile(percentile: number | null): string {
  if (percentile == null || !Number.isFinite(percentile)) {
    return EMPTY_VALUE;
  }
  return `Top ${Math.max(1, Math.round(percentile))}%`;
}

export function formatConfidence(confidence: number | null): string {
  if (confidence == null || !Number.isFinite(confidence)) {
    return EMPTY_VALUE;
  }
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Display-only casing for svc-support's authority keywords, which arrive lower-cased.
 * Tribunals and regulators read badly title-cased ("Accc"), so the acronyms are spelled out.
 */
const AUTHORITY_ACRONYMS = new Set(['accc', 'vcat', 'ncat', 'qcat', 'afca']);

function formatKeyword(keyword: string): string {
  return keyword
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      AUTHORITY_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

export function formatKeywordList(keywords: Array<string>): string {
  if (keywords.length === 0) {
    return EMPTY_VALUE;
  }
  return keywords.map(formatKeyword).join(', ');
}

/**
 * Machine prefixes on Zendesk risk-flag and classification tags that say nothing on screen —
 * `zd_rt_insights_churn_risk` reads as "Churn risk" without losing anything.
 */
const TAG_MACHINE_PREFIXES = ['zd_rt_insights_', 'ai_categorised_ticket_', 'ticket_reason_'];

/** `zd_rt_insights_churn_risk` → `Churn risk`; `cs::enquiry::customer::promo_code_offered` → `Promo code offered`. */
export function formatRiskFlagLabel(tag: string): string {
  // cs:: macro tags nest the meaningful part in the last segment.
  const lastSegment = tag.split('::').pop() ?? tag;
  const stripped = TAG_MACHINE_PREFIXES.reduce(
    (value, prefix) => (value.startsWith(prefix) ? value.slice(prefix.length) : value),
    lastSegment,
  );

  return (humanize(stripped) ?? tag).replace(/\bSla\b/g, 'SLA');
}

/** `authority_legal` → `Authority legal`; `reason:Complaint` → `Reason: Complaint`. */
export function formatBreakdownLabel(value: string | null | undefined): string {
  if (!value) {
    return EMPTY_VALUE;
  }
  const [head, ...rest] = value.split(':');
  const label = humanize(head) ?? head;

  if (rest.length === 0) {
    return label;
  }
  const detail = rest.join(':').trim();

  // Tag sources are raw Zendesk machine tags; reasons and subcategories are already human.
  return `${label}: ${head === 'tag' ? formatRiskFlagLabel(detail) : detail}`;
}

/**
 * `breakdown` is a JSON blob persisted per scoring run and svc-support reshapes it as the
 * scoring model evolves, so a row can legitimately arrive without a component the UI knows
 * about. Reading through these defaults renders zeros and empty lists instead of throwing —
 * a stale contract used to take the whole dashboard down with it.
 */
const HARD_BREAKDOWN_DEFAULTS: TicketRiskHardBreakdown = {
  classificationScore: 0,
  classificationSource: null,
  classificationLift: null,
  riskFlagScore: 0,
  matchedRiskFlags: [],
  keywordSignalScore: 0,
  matchedKeywordSignals: [],
  legalHold: false,
  reassignPoints: 0,
  agingPoints: 0,
  awaitingReviewPoints: 0,
  requesterWaitPoints: 0,
  staleContactPoints: 0,
  internalCommentPoints: 0,
  reopenPoints: 0,
  operationalScore: 0,
  inStayPoints: 0,
  upcomingCheckInPoints: 0,
  recentStayPoints: 0,
  ltvPoints: 0,
  societePoints: 0,
  luxPlusPoints: 0,
  customerScore: 0,
  hardScore: 0,
};

export function zendeskTicketUrl(ticketId: string): string {
  return `${ZENDESK_TICKET_URL}/${ticketId}?brand_id=${ZENDESK_BRAND_ID}`;
}

/**
 * @param currentUserId the signed-in leader's `id_member`, so their own watch can be picked out;
 *   null when unknown (nobody reads as "me").
 */
export function toCxTicketRow(scored: ScoredTicketRisk, currentUserId: string | null = null): CxTicketRow {
  const { ticket, customer } = scored.breakdown.factBlock;
  const { llm } = scored.breakdown;
  const hard = { ...HARD_BREAKDOWN_DEFAULTS, ...scored.breakdown.hard };
  const watcher = scored.watch ?? null;

  return {
    id: scored.ticketId,
    subject: ticket.subject,

    riskScore: scored.score,
    riskTier: bandToTier(scored.band),
    isNew: scored.isNew ?? false,
    tierChange: scored.tierChange ?? null,
    previousTier: scored.previousBand ? bandToTier(scored.previousBand) : null,
    watcher,
    watcherName: watcher?.watcherName ?? '',
    isWatchedByMe: watcher !== null && currentUserId !== null && watcher.watcherId === currentUserId,
    commentCount: scored.commentCount ?? 0,
    recentCommentCount: scored.recentCommentCount ?? 0,
    lastComment: scored.lastComment ?? null,
    hardScore: scored.hardScore,
    llmScore: scored.llmScore ?? null,
    divergent: scored.divergent,
    percentile: scored.percentile ?? null,
    criticalRank: scored.criticalRank ?? null,
    hasLlmAssessment: llm !== null,
    llmTrajectory: llm?.trajectory ?? null,
    llmConfidence: llm?.confidence ?? null,
    llmReasoning: llm?.reasoning ?? null,
    llmRiskFactors: llm?.riskFactors ?? [],
    llmEarlyWarnings: llm?.earlyWarnings ?? [],
    resolutionAccepted: llm?.resolutionAccepted ?? false,

    classificationScore: hard.classificationScore,
    classificationSource: hard.classificationSource,
    classificationLift: hard.classificationLift,
    riskFlagScore: hard.riskFlagScore,
    matchedRiskFlags: hard.matchedRiskFlags ?? [],
    keywordSignalScore: hard.keywordSignalScore,
    matchedKeywordSignals: hard.matchedKeywordSignals ?? [],
    legalHold: hard.legalHold,
    reassignPoints: hard.reassignPoints,
    agingPoints: hard.agingPoints,
    awaitingReviewPoints: hard.awaitingReviewPoints,
    requesterWaitPoints: hard.requesterWaitPoints,
    staleContactPoints: hard.staleContactPoints,
    internalCommentPoints: hard.internalCommentPoints,
    reopenPoints: hard.reopenPoints,
    operationalRiskScore: hard.operationalScore,
    inStayPoints: hard.inStayPoints,
    upcomingCheckInPoints: hard.upcomingCheckInPoints,
    recentStayPoints: hard.recentStayPoints,
    ltvPoints: hard.ltvPoints,
    societePoints: hard.societePoints,
    luxPlusPoints: hard.luxPlusPoints,
    customerRiskScore: hard.customerScore,

    created: ticket.createdAt,
    lastUpdated: ticket.updatedAt,
    statusUpdated: ticket.statusUpdatedAt,
    ticketStatus: toTicketStatusLabel(ticket.status, ticket.customStatusName),
    team: ticket.groupName,
    assignee: ticket.assigneeName,
    teamLeader: ticket.teamLeader ?? null,
    ticketReason: ticket.ticketReason,
    subcategory: ticket.subcategory,
    triageCategory: ticket.triageCategory,
    priority: ticket.priority,
    tags: ticket.tags ?? [],
    replies: ticket.publicComments,
    internalComments: ticket.internalComments,
    agentsTouched: ticket.agentsTouched,
    reassignCount: ticket.reassignCount,
    reopenCount: ticket.reopens,
    requesterWaitMinutes: ticket.requesterWaitMinutes,
    caseAgeDays: Math.floor(ticket.hoursSinceOpen / HOURS_PER_DAY),
    hoursSinceOpen: ticket.hoursSinceOpen,
    hoursAwaitingReview: ticket.hoursAwaitingReview,
    hoursSinceLastContact: ticket.hoursSinceLastContact,
    lastAgentReplyAt: ticket.lastAgentReplyAt,
    sentiment: humanize(ticket.zendeskSentiment),
    sentimentBucket: toSentimentBucket(ticket.zendeskSentiment),

    journeyPhase: toJourneyPhase(customer.inStayNow, customer.upcomingCheckInDays),
    checkInDays: customer.upcomingCheckInDays,
    societeTier: toSocieteTierLabel(customer.societeTier),
    luxPlusMember: customer.luxPlusMember,
    customerLifetimeValue: customer.ltvAud,
    ltvBand: humanize(customer.ltvBand),
    daysSinceLastCheckOut: customer.daysSinceLastCheckOut ?? null,
    authorityKeywords: customer.authorityKeywordHits ?? [],
    authorityConfirmed: llm?.authorityConfirmed ?? false,
  };
}

export function toCxTicketRows(
  report: TicketRiskReport | undefined,
  currentUserId: string | null = null,
): Array<CxTicketRow> {
  return (report?.tickets ?? []).map((scored) => toCxTicketRow(scored, currentUserId));
}
